// CogniPrint System Diagnostic — deterministic, no AI
// Runs on login to verify data integrity and auto-fix safe issues.
// ~5 Firestore reads per run, no Claude calls.

import {
  doc,
  getDoc,
  getDocs,
  collection,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  updateDoc,
} from 'firebase/firestore';
import { db } from './firebase';
import { COLLECTIONS } from '../constants/collections';
import type { CogniPrintProfile, ContentFormatPreference, SequencingPreference, MotivationalDriver } from '../types/cogniprint';
import type { LearningPath } from '../types/learning-path';

export type DiagnosticStatus = 'healthy' | 'warning' | 'action_needed';
export type CheckStatus = 'pass' | 'warning' | 'fail';

export interface DiagnosticCheck {
  name: string;
  status: CheckStatus;
  message: string | null;
  autoFixable: boolean;
  autoFixed: boolean;
}

export interface DiagnosticResult {
  status: DiagnosticStatus;
  checks: DiagnosticCheck[];
  timestamp: string;
}

const WEIGHTED_SUM_TOLERANCE = 2;
const MAX_STALE_ANALYTICS_DAYS = 90;

function rollup(checks: DiagnosticCheck[]): DiagnosticStatus {
  const hasFail = checks.some((c) => c.status === 'fail' && !c.autoFixed);
  if (hasFail) return 'action_needed';
  const hasWarning = checks.some((c) => c.status === 'warning' && !c.autoFixed);
  if (hasWarning) return 'warning';
  return 'healthy';
}

function sumWeighted(obj: Record<string, number>): number {
  return Object.values(obj).reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0);
}

function normalizeWeighted<T extends Record<string, number>>(obj: T): T {
  const total = sumWeighted(obj);
  if (total === 0) return obj;
  const scale = 100 / total;
  const next = {} as T;
  for (const key of Object.keys(obj) as Array<keyof T>) {
    (next as Record<string, number>)[key as string] = Math.round((obj[key] as number) * scale);
  }
  return next;
}

function inRange(value: number, min: number, max: number): boolean {
  return typeof value === 'number' && !Number.isNaN(value) && value >= min && value <= max;
}

// ─── Check 1: Profile integrity ───

async function checkProfileIntegrity(userId: string): Promise<DiagnosticCheck> {
  const ref = doc(db, COLLECTIONS.COGNIPRINTS, userId);
  const snap = await getDoc(ref);

  if (!snap.exists()) {
    return {
      name: 'Profile integrity',
      status: 'warning',
      message: 'No CogniPrint profile yet. Take the diagnostic to create one.',
      autoFixable: false,
      autoFixed: false,
    };
  }

  const profile = snap.data() as CogniPrintProfile;
  const issues: string[] = [];
  const fixes: Record<string, unknown> = {};

  if (typeof profile.profileVersion !== 'number' || profile.profileVersion < 1) {
    issues.push('invalid profileVersion');
    fixes.profileVersion = 1;
  }

  const depth = profile.depthTolerance?.score;
  if (typeof depth === 'number' && !inRange(depth, 0, 100)) {
    issues.push('depthTolerance out of range');
    fixes['depthTolerance.score'] = Math.max(0, Math.min(100, depth));
  }

  const cf = profile.contentFormat?.score as ContentFormatPreference | undefined;
  if (cf) {
    const cfSum = sumWeighted(cf as unknown as Record<string, number>);
    if (Math.abs(cfSum - 100) > WEIGHTED_SUM_TOLERANCE && cfSum > 0) {
      issues.push('contentFormat weights do not sum to 100');
      fixes.contentFormat = {
        ...profile.contentFormat,
        score: normalizeWeighted(cf as unknown as Record<string, number>),
      };
    }
  }

  const seq = profile.sequencing?.score as SequencingPreference | undefined;
  if (seq) {
    const seqSum = sumWeighted(seq as unknown as Record<string, number>);
    if (Math.abs(seqSum - 100) > WEIGHTED_SUM_TOLERANCE && seqSum > 0) {
      issues.push('sequencing weights do not sum to 100');
      fixes.sequencing = {
        ...profile.sequencing,
        score: normalizeWeighted(seq as unknown as Record<string, number>),
      };
    }
  }

  const mot = profile.motivation?.score as MotivationalDriver | undefined;
  if (mot) {
    const motSum = sumWeighted(mot as unknown as Record<string, number>);
    if (Math.abs(motSum - 100) > WEIGHTED_SUM_TOLERANCE && motSum > 0) {
      issues.push('motivation weights do not sum to 100');
      fixes.motivation = {
        ...profile.motivation,
        score: normalizeWeighted(mot as unknown as Record<string, number>),
      };
    }
  }

  if (issues.length === 0) {
    return {
      name: 'Profile integrity',
      status: 'pass',
      message: null,
      autoFixable: false,
      autoFixed: false,
    };
  }

  let autoFixed = false;
  if (Object.keys(fixes).length > 0) {
    try {
      await updateDoc(ref, fixes);
      autoFixed = true;
    } catch (err) {
      console.error('systemDiagnostic: profile auto-fix failed', err);
    }
  }

  return {
    name: 'Profile integrity',
    status: autoFixed ? 'pass' : 'warning',
    message: `Profile had ${issues.length} issue(s): ${issues.join(', ')}`,
    autoFixable: true,
    autoFixed,
  };
}

// ─── Check 2: Subscription consistency ───

async function checkSubscriptionConsistency(userId: string): Promise<DiagnosticCheck> {
  const userSnap = await getDoc(doc(db, COLLECTIONS.USERS, userId));

  if (!userSnap.exists()) {
    return {
      name: 'Subscription consistency',
      status: 'fail',
      message: 'User document missing.',
      autoFixable: false,
      autoFixed: false,
    };
  }

  const data = userSnap.data();
  const tier = data.subscription ?? 'free';
  const subData = data.subscriptionData ?? {};
  const issues: string[] = [];
  const fixes: Record<string, unknown> = {};

  const uploadsUsed = subData.uploadsUsedThisPeriod;
  if (typeof uploadsUsed === 'number' && uploadsUsed < 0) {
    issues.push('negative upload counter');
    fixes['subscriptionData.uploadsUsedThisPeriod'] = 0;
  }

  const tutorUsed = subData.tutorQueriesUsedThisPeriod;
  if (typeof tutorUsed === 'number' && tutorUsed < 0) {
    issues.push('negative tutor query counter');
    fixes['subscriptionData.tutorQueriesUsedThisPeriod'] = 0;
  }

  if (tier !== 'free' && subData.currentPeriodEnd) {
    const periodEnd = new Date(subData.currentPeriodEnd).getTime();
    if (!Number.isNaN(periodEnd) && periodEnd < Date.now() && !subData.cancelAtPeriodEnd) {
      issues.push('billing period expired without renewal');
    }
  }

  if (issues.length === 0) {
    return {
      name: 'Subscription consistency',
      status: 'pass',
      message: null,
      autoFixable: false,
      autoFixed: false,
    };
  }

  let autoFixed = false;
  if (Object.keys(fixes).length > 0) {
    try {
      await updateDoc(doc(db, COLLECTIONS.USERS, userId), fixes);
      autoFixed = Object.keys(fixes).length === issues.length;
    } catch (err) {
      console.error('systemDiagnostic: subscription auto-fix failed', err);
    }
  }

  return {
    name: 'Subscription consistency',
    status: autoFixed ? 'pass' : 'warning',
    message: `Subscription had ${issues.length} issue(s): ${issues.join(', ')}`,
    autoFixable: Object.keys(fixes).length > 0,
    autoFixed,
  };
}

// ─── Check 3: Learning path health ───

async function checkLearningPathHealth(userId: string): Promise<DiagnosticCheck> {
  const q = query(
    collection(db, COLLECTIONS.LEARNING_PATHS),
    where('userId', '==', userId),
    firestoreLimit(20),
  );
  const snap = await getDocs(q);

  if (snap.empty) {
    return {
      name: 'Learning path health',
      status: 'pass',
      message: null,
      autoFixable: false,
      autoFixed: false,
    };
  }

  let nodesNeedingRegen = 0;
  let brokenReferences = 0;

  for (const pathDoc of snap.docs) {
    const path = pathDoc.data() as LearningPath;
    if (!path.contentId) {
      brokenReferences++;
      continue;
    }
    const nodes = path.nodes ?? [];
    for (const node of nodes) {
      if (node.contentGenerated === false && path.generationStatus === 'complete') {
        nodesNeedingRegen++;
      }
    }
  }

  if (brokenReferences === 0 && nodesNeedingRegen === 0) {
    return {
      name: 'Learning path health',
      status: 'pass',
      message: null,
      autoFixable: false,
      autoFixed: false,
    };
  }

  const parts: string[] = [];
  if (brokenReferences > 0) parts.push(`${brokenReferences} path(s) with broken content reference`);
  if (nodesNeedingRegen > 0) parts.push(`${nodesNeedingRegen} node(s) missing generated content`);

  return {
    name: 'Learning path health',
    status: brokenReferences > 0 ? 'fail' : 'warning',
    message: parts.join('; '),
    autoFixable: false,
    autoFixed: false,
  };
}

// ─── Check 4: Stale data cleanup ───

async function checkStaleData(userId: string): Promise<DiagnosticCheck> {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - MAX_STALE_ANALYTICS_DAYS);
  const cutoffTs = cutoff.getTime();

  const q = query(
    collection(db, 'analytics', userId, 'events'),
    orderBy('timestamp', 'asc'),
    firestoreLimit(1),
  );
  const snap = await getDocs(q);

  if (snap.empty) {
    return {
      name: 'Stale data cleanup',
      status: 'pass',
      message: null,
      autoFixable: false,
      autoFixed: false,
    };
  }

  const oldestTs = (snap.docs[0].data().timestamp as number) ?? 0;
  if (oldestTs < cutoffTs) {
    return {
      name: 'Stale data cleanup',
      status: 'warning',
      message: `Analytics events older than ${MAX_STALE_ANALYTICS_DAYS} days are eligible for archival.`,
      autoFixable: false,
      autoFixed: false,
    };
  }

  return {
    name: 'Stale data cleanup',
    status: 'pass',
    message: null,
    autoFixable: false,
    autoFixed: false,
  };
}

// ─── Check 5: Adaptive engine state ───

async function checkAdaptiveEngineState(userId: string): Promise<DiagnosticCheck> {
  const q = query(
    collection(db, 'adaptive', userId, 'history'),
    orderBy('timestamp', 'desc'),
    firestoreLimit(5),
  );
  const snap = await getDocs(q);

  if (snap.empty) {
    return {
      name: 'Adaptive engine state',
      status: 'pass',
      message: null,
      autoFixable: false,
      autoFixed: false,
    };
  }

  const entries = snap.docs.map((d) => d.data() as { version?: number; timestamp?: string });
  const versions = entries
    .map((e) => e.version)
    .filter((v): v is number => typeof v === 'number');

  if (versions.length >= 2) {
    const sorted = [...versions].sort((a, b) => b - a);
    for (let i = 0; i < sorted.length - 1; i++) {
      if (sorted[i] <= sorted[i + 1]) {
        return {
          name: 'Adaptive engine state',
          status: 'warning',
          message: 'Recalibration versions are not strictly increasing.',
          autoFixable: false,
          autoFixed: false,
        };
      }
    }
  }

  return {
    name: 'Adaptive engine state',
    status: 'pass',
    message: null,
    autoFixable: false,
    autoFixed: false,
  };
}

// ─── Orchestrator ───

export async function runSystemDiagnostic(userId: string): Promise<DiagnosticResult> {
  const results = await Promise.allSettled([
    checkProfileIntegrity(userId),
    checkSubscriptionConsistency(userId),
    checkLearningPathHealth(userId),
    checkStaleData(userId),
    checkAdaptiveEngineState(userId),
  ]);

  const checks: DiagnosticCheck[] = results.map((r, i) => {
    if (r.status === 'fulfilled') return r.value;
    const names = ['Profile integrity', 'Subscription consistency', 'Learning path health', 'Stale data cleanup', 'Adaptive engine state'];
    return {
      name: names[i],
      status: 'warning' as CheckStatus,
      message: `Check failed to run: ${r.reason instanceof Error ? r.reason.message : 'unknown error'}`,
      autoFixable: false,
      autoFixed: false,
    };
  });

  return {
    status: rollup(checks),
    checks,
    timestamp: new Date().toISOString(),
  };
}
